import * as v from 'valibot'
import short from 'short-uuid'

export type UuidFormatType = 'full' | 'short'

function getUuidSchema_V2(params: {
	expected_format: UuidFormatType
	convert_format?: boolean
	error_message?: string
	error_field_name?: string
}) {
	const { expected_format, convert_format = false, error_field_name = 'value' } = params
	const {
		error_message = `${error_field_name} is not a valid ${expected_format === 'short' ? 'short UUID' : 'UUID'}`,
	} = params

	const isValidShortUuid = v.check((value: string) => {
		try {
			short().toUUID(value)
		} catch {
			return false
		}

		return true
	}, error_message)

	if (expected_format === 'short') {
		return convert_format
			? v.pipe(
					v.string(error_message),
					v.trim(),
					v.length(22, error_message),
					isValidShortUuid,
					v.transform((value) => short().toUUID(value).toString()),
					v.uuid(error_message),
				)
			: v.pipe(v.string(), v.trim(), v.length(22, error_message), isValidShortUuid)
	}

	if (expected_format === 'full' && convert_format) {
		return v.pipe(
			v.string(error_message),
			v.uuid(error_message),
			v.transform((value) => short().fromUUID(value).toString()),
			isValidShortUuid,
		)
	}

	return v.pipe(v.string(), v.uuid(error_message))
}

// True iff value is a minimized short UUID (22-char base62 that short-uuid can expand) — mirrors the
// short-format check in getUuidSchema_V2 so callers can branch without round-tripping through v.parse.
export function isShortUuid(value: unknown): value is string {
	if (typeof value !== 'string' || value.length !== 22) return false
	try {
		short().toUUID(value)
		return true
	} catch {
		return false
	}
}

function getUuidSchema(
	params: { error_message?: string; expect_short_uuid?: boolean; convert_to_full_uuid?: boolean } = {},
) {
	const { expect_short_uuid = false, convert_to_full_uuid = false } = params
	const { error_message = `value is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}` } = params

	if (expect_short_uuid) {
		const isValidShortUuid = v.check((value: string) => {
			try {
				short().toUUID(value)
			} catch {
				return false
			}

			return true
		}, error_message)

		return convert_to_full_uuid
			? v.pipe(
					v.string(),
					isValidShortUuid,
					v.transform((value) => short().toUUID(value).toString()),
					v.uuid(error_message),
				)
			: v.pipe(v.string(), isValidShortUuid)
	}

	return v.pipe(v.string(), v.uuid(error_message), v.string())
}

function getNonEmptyStringSchema(params: { error_message?: string; error_field_name?: string } = {}) {
	const { error_message = `${params.error_field_name ?? 'value'} cannot be an empty string` } = params

	return v.pipe(v.string(), v.trim(), v.nonEmpty(error_message))
}

function getTextToBoolSchema(params: { default_value: boolean; is_optional?: boolean }) {
	const { default_value, is_optional = false } = params

	const mappings: Record<string, boolean> = {
		true: true,
		yes: true,
		'1': true,
		false: false,
		no: false,
		'0': false,
	}

	const schema = v.pipe(
		v.string(),
		v.trim(),
		v.transform((str_val) => mappings[str_val.toLowerCase()] ?? default_value),
	)

	return is_optional ? v.optional(schema, String(default_value)) : schema
}

/**
 * Returns a Valibot union schema that accepts either a Date object or an ISO
 * formatted string. If an ISO string is passed, a Date object will be returned.
 */
function getDateOrIsoStringSchema(params: { error_message?: string } = {}) {
	const { error_message = 'Value is not a Date object or valid ISO string' } = params

	return v.union(
		[
			v.date(),
			v.pipe(
				v.string(),
				v.isoTimestamp(),
				v.transform((str_val) => new Date(str_val)),
			),
		],
		error_message,
	)
}

function getIsoDateFromDateOrIsoStringSchema(
	params: { input_error_message?: string; transform_error_message?: string; error_field_name?: string } = {},
) {
	const { error_field_name = 'Value' } = params
	const {
		input_error_message = `${error_field_name} is not a Date object or valid ISO string`,
		transform_error_message = `${error_field_name} could not be transformed to an ISO date`,
	} = params

	return v.union([
		v.pipe(v.string(), v.isoDate()),
		v.pipe(
			getDateOrIsoStringSchema({ error_message: input_error_message }),
			v.transform((date_obj) => date_obj.toISOString().split('T')[0]),
			v.isoDate(transform_error_message),
		),
	])
}

/**
 * Returns a Valibot schema that accepts a string and determines if it is a valid UUIDv4.
 * If a UUIDv4 value with dashes removed is passed in, the dashes will be restored.
 */
function getUuidRestorationschema(params: { error_message?: string } = {}) {
	const { error_message = 'Value is not a valid UUIDv4 string' } = params

	return v.pipe(
		v.string(),
		// If the input is exactly 32 characters it could be a UUIDv4 without dashes, so we should restore them
		v.transform((input) =>
			input.length === 32 ? input.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/g, '$1-$2-$3-$4-$5') : input,
		),
		v.uuid(error_message),
	)
}

function mergeErrorsToString(
	result_obj: v.SafeParseResult<v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>>,
	params: { separator?: string; use_nested_field_names?: boolean; default_error_message?: string } = {},
) {
	const {
		separator = '; ',
		use_nested_field_names = true,
		default_error_message = 'An error occurred during schema validation',
	} = params

	if (result_obj.success || !result_obj.issues) {
		return ''
	}

	let all_issues: string[] = []

	const flat_issues_obj = v.flatten(result_obj.issues)

	// handle root issues
	if (Array.isArray(flat_issues_obj.root)) {
		all_issues = [...all_issues, ...flat_issues_obj.root.flat()]
	}

	// handle nested issues
	if (flat_issues_obj.nested) {
		for (const [key, value] of Object.entries(flat_issues_obj.nested)) {
			const nested_issues = Array.isArray(value)
				? value.flat().map((issue) => (use_nested_field_names ? `${key}: ${issue}` : issue))
				: []

			all_issues = [...all_issues, ...nested_issues]
		}
	}

	// handle other issues
	if (Array.isArray(flat_issues_obj.other)) {
		all_issues = [...all_issues, ...flat_issues_obj.other.flat()]
	}

	return all_issues.length ? all_issues.join(separator) : default_error_message
}

function getFormattedErrorPath(err: v.BaseIssue<unknown>) {
	if (!Array.isArray(err.path) || !err.path.length) {
		return ''
	}

	const parts = err.path.map((part, index) => {
		let fmt_part = part.key

		switch (part.type) {
			case 'array':
				fmt_part = `[${part.key}]`
				break

			case 'object':
				fmt_part = `${index > 0 ? '.' : ''}${part.key}`
				break

			default:
				break
		}

		return fmt_part
	})

	return parts.join('')
}

export const TimezoneIANASchema = v.pipe(
  v.string(),
  v.check((tz) => {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, 'Invalid IANA timezone')
);
export type TimezoneIANA = v.InferOutput<typeof TimezoneIANASchema>


function getSimpleValidationErrors(issues: ReturnType<typeof v.safeParse>['issues']) {
	if (!issues) {
		return []
	}

	const validation_issues = issues.map((issue) => ({
		path: v.getDotPath(issue),
		message: issue.message,
		received: issue.received,
	}))

	return validation_issues
}

const ValibotCommon = {
	getNonEmptyStringSchema,
	getUuidSchema,
	getUuidSchema_V2,
	getTextToBoolSchema,
	getDateOrIsoStringSchema,
	getIsoDateFromDateOrIsoStringSchema,
	getUuidRestorationschema,
	mergeErrorsToString,
	getFormattedErrorPath,
	getSimpleValidationErrors,
}

export default ValibotCommon
